import dayjs, { Dayjs } from "dayjs";

type DayEntry<T extends { combinationId: string }> = {
  date: string;
  bookings: T[];
};

type CompressedBooking<T extends { combinationId: string }> = Omit<T, "combinationId"> & {
  combinationId: string;
  start: Dayjs;
  end: Dayjs;
};

export function compressBookingsByAdjacentDays<T extends { combinationId: string }>(days: DayEntry<T>[]): CompressedBooking<T>[] {
  const active = new Map<
    string,
    {
      start: Dayjs;
      end: Dayjs;
      rest: Omit<T, "combinationId">;
    }
  >();

  const result: CompressedBooking<T>[] = [];

  for (const day of days) {
    const currentDate = dayjs(day.date).startOf("day");

    for (const booking of day.bookings) {
      const { combinationId, ...rest } = booking;
      const existing = active.get(combinationId);

      if (!existing) {
        active.set(combinationId, {
          start: currentDate,
          end: currentDate,
          rest,
        });
        continue;
      }

      const diffDays = currentDate.diff(existing.end, "day");

      if (diffDays === 1) {
        existing.end = currentDate;
      } else if (diffDays > 1) {
        result.push({
          combinationId,
          start: existing.start,
          end: existing.end,
          ...existing.rest,
        } as CompressedBooking<T>);

        active.set(combinationId, {
          start: currentDate,
          end: currentDate,
          rest,
        });
      }
    }
  }

  // flush
  for (const [combinationId, range] of active) {
    result.push({
      combinationId,
      start: range.start,
      end: range.end,
      ...range.rest,
    } as CompressedBooking<T>);
  }

  return result;
}
